import React, { FC, useEffect, useRef, useMemo, useState } from "react";
import styled from "styled-components";
import { rem, vwDesktop, vwMobile } from "../../helpers/styles";
import { MEDIA_DESKTOP } from "../../helpers/theme";
import SectionLines from "../SectionLines";

type VideoSource =
  | { type: "youtube"; url: string }
  | { type: "asset"; url: string };

export type FiftyFiftyVideoProps = {
  headline: string;
  descriptionHtml: string;
  ctaLabel: string;
  ctaUrl: string;

  video?: VideoSource;
  thumbnailUrl?: string;
  thumbnailAlt?: string;

  className?: string;
};

function getYouTubeEmbedUrl(inputUrl: string) {
  // Handles: youtu.be/<id>, youtube.com/watch?v=<id>, youtube.com/embed/<id>
  try {
    const url = new URL(inputUrl);

    // youtu.be/<id>
    if (url.hostname.includes("youtu.be")) {
      const id = url.pathname.replace("/", "");
      return `https://www.youtube.com/embed/${id}`;
    }

    // youtube.com/embed/<id>
    if (url.pathname.startsWith("/embed/")) {
      return `https://www.youtube.com${url.pathname}`;
    }

    // youtube.com/watch?v=<id>
    const v = url.searchParams.get("v");
    if (v) return `https://www.youtube.com/embed/${v}`;

    return inputUrl; // fallback
  } catch {
    return inputUrl;
  }
}

// Basic focus trap
function useFocusTrap(
  isOpen: boolean,
  containerRef: React.RefObject<HTMLElement>,
) {
  useEffect(() => {
    if (!isOpen) return;

    const el = containerRef.current;
    if (!el) return;

    const focusableSelector =
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

    const getFocusable = () =>
      Array.from(el.querySelectorAll<HTMLElement>(focusableSelector)).filter(
        (n) => !n.hasAttribute("disabled") && !n.getAttribute("aria-hidden"),
      );

    const focusables = getFocusable();
    (focusables[0] ?? el).focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      const items = getFocusable();
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, containerRef]);
}

export const FiftyFiftyVideo: FC<FiftyFiftyVideoProps> = ({
  headline,
  descriptionHtml,
  ctaLabel,
  ctaUrl,
  video,
  thumbnailUrl,
  thumbnailAlt = "Play video",
  className,
}: FiftyFiftyVideoProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const titleIdRef = useRef(
    `fifty-fifty-video-${Math.random().toString(36).slice(2, 9)}`,
  );
  const titleId = titleIdRef.current;
  const dialogId = `${titleId}-dialog`;
  const dialogTitleId = `${dialogId}-title`;
  const wasOpenRef = useRef(false);

  const embedUrl = useMemo(() => {
    if (!video || video?.type !== "youtube") return "";
    return getYouTubeEmbedUrl(video?.url);
  }, [video]);

  useFocusTrap(isOpen, dialogRef);

  // ESC to close + restore focus to trigger
  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;

      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";

      return () => {
        document.body.style.overflow = prevOverflow;
      };
    }

    if (wasOpenRef.current) {
      triggerRef.current?.focus();
    }
  }, [isOpen]);

  return (
    <>
      <Section className={className} aria-labelledby={titleId}>
        <Inner>
          <Grid>
            <ContentCol>
              <Headline id={titleId}>{headline}</Headline>

              <Description
                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
              />

              <CtaWrap>
                <PrimaryCta href={ctaUrl}>{ctaLabel}</PrimaryCta>
              </CtaWrap>
            </ContentCol>

            <VideoCol>
              {video ? (
                <VideoTrigger
                  type="button"
                  onClick={() => setIsOpen(true)}
                  aria-haspopup="dialog"
                  aria-controls={dialogId}
                  aria-label="Open video"
                  ref={triggerRef}
                  aria-expanded={isOpen}
                >
                  <Thumb src={thumbnailUrl} alt={thumbnailAlt} />
                  <PlayBadge aria-hidden="true">
                    <PlayCircle>
                      <PlayIcon
                        viewBox="0 0 40 40"
                        focusable="false"
                        aria-hidden="true"
                        preserveAspectRatio="xMidYMid meet"
                      >
                        <path
                          d="M17.3231 26.3583L25.9596 20.8142C26.3074 20.5907 26.4813 20.2712 26.4813 19.8558C26.4813 19.4401 26.3074 19.1177 25.9596 18.8886L17.3231 13.3446C16.9187 13.0788 16.5173 13.0591 16.1188 13.2856C15.7199 13.512 15.5205 13.8526 15.5205 14.3074V25.3955C15.5205 25.8502 15.7199 26.1908 16.1188 26.4173C16.5173 26.6437 16.9187 26.6241 17.3231 26.3583ZM19.8586 39.7029C17.122 39.7029 14.5526 39.1819 12.1502 38.1401C9.74779 37.0983 7.64354 35.6752 5.83742 33.8709C4.03131 32.0667 2.60697 29.9644 1.5644 27.5643C0.521467 25.1641 0 22.5955 0 19.8586C0 17.1136 0.520916 14.5327 1.56275 12.116C2.60458 9.69963 4.02763 7.59758 5.83191 5.80985C7.63618 4.02212 9.73841 2.60697 12.1386 1.5644C14.5388 0.521467 17.1073 0 19.8443 0C22.5893 0 25.1701 0.520918 27.5869 1.56275C30.0032 2.60458 32.1053 4.01844 33.893 5.80434C35.6807 7.59023 37.0959 9.69044 38.1385 12.105C39.1814 14.5191 39.7029 17.0989 39.7029 19.8443C39.7029 22.5808 39.1819 25.1503 38.1401 27.5527C37.0983 29.9551 35.6844 32.0593 33.8985 33.8654C32.1126 35.6715 30.0124 37.0959 27.5979 38.1385C25.1837 39.1814 22.604 39.7029 19.8586 39.7029ZM19.8514 38.0061C24.9018 38.0061 29.1902 36.2408 32.7168 32.7102C36.243 29.1796 38.0061 24.8933 38.0061 19.8514C38.0061 14.8011 36.243 10.5126 32.7168 6.98605C29.1902 3.45985 24.9018 1.69675 19.8514 1.69675C14.8095 1.69675 10.5233 3.45985 6.99267 6.98605C3.46205 10.5126 1.69675 14.8011 1.69675 19.8514C1.69675 24.8933 3.46205 29.1796 6.99267 32.7102C10.5233 36.2408 14.8095 38.0061 19.8514 38.0061Z"
                          fill="currentColor"
                        />
                      </PlayIcon>
                    </PlayCircle>
                  </PlayBadge>
                </VideoTrigger>
              ) : null}
            </VideoCol>
          </Grid>
        </Inner>
      </Section>

      {isOpen && (
        <Overlay
          role="presentation"
          onMouseDown={(e) => {
            // close only if clicking the overlay backdrop, not the dialog
            if (e.target === e.currentTarget) setIsOpen(false);
          }}
        >
          <Dialog
            id={dialogId}
            role="dialog"
            aria-modal="true"
            aria-labelledby={dialogTitleId}
            ref={dialogRef}
          >
            <VisuallyHidden id={dialogTitleId}>{headline}</VisuallyHidden>

            <CloseButton
              type="button"
              onClick={() => setIsOpen(false)}
              aria-label="Close video"
            >
              ✕
            </CloseButton>

            <PlayerWrap>
              {video?.type === "youtube" ? (
                <Iframe
                  src={`${embedUrl}?autoplay=1&rel=0`}
                  title={headline}
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <Html5Video controls autoPlay>
                  <source src={video?.url} />
                  Your browser does not support the video tag.
                </Html5Video>
              )}
            </PlayerWrap>
          </Dialog>
        </Overlay>
      )}
    </>
  );
};

const Section = styled.section`
  position: relative;
  background: #b7db74;
  padding-top: ${vwMobile(80)};
  padding-bottom: ${vwMobile(80)};
  padding-left: ${vwMobile(32)};
  padding-right: ${vwMobile(32)};
  ${MEDIA_DESKTOP} {
    padding-top: ${vwDesktop(120)};
    padding-bottom: ${vwDesktop(120)};
    padding-left: ${vwDesktop(128)};
    padding-right: ${vwDesktop(80)};
  }

  ${SectionLines("#012604")};
`;

const Inner = styled.div`
  max-width: ${vwDesktop(1280)};
  margin: 0 auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${vwMobile(50)};
  align-items: center;

  ${MEDIA_DESKTOP} {
    grid-template-columns: 1fr 1fr;
    gap: ${vwDesktop(48)};
  }
`;

const ContentCol = styled.div`
  order: 2;
  padding-right: ${vwMobile(20)};
  padding-left: ${vwMobile(20)};

  ${MEDIA_DESKTOP} {
    order: 1;
    padding-right: ${vwDesktop(106)};
    padding-left: 0;
  }
`;

const VideoCol = styled.div`
  order: 1;

  ${MEDIA_DESKTOP} {
    order: 2;
  }
`;

const Headline = styled.h2`
  margin-left: ${vwMobile(8)};
  margin-bottom: ${vwMobile(8)};
  color: #012604;
  font-family: "GT Alpina";
  font-weight: 300;
  text-align: center;
  font-size: ${rem(48)};
  line-height: 100%;
  letter-spacing: ${vwMobile(-2.4)};
  max-width: ${vwMobile(340)};

  ${MEDIA_DESKTOP} {
    margin-bottom: ${vwDesktop(40)};
    font-size: ${rem(72)};
    line-height: 95%;
    letter-spacing: ${vwDesktop(-3.6)};
    max-width: ${vwDesktop(520)};
    text-align: left;
    margin-left: 0;
    margin-right: ${vwDesktop(78)};
  }
`;

const Description = styled.div`
  color: #012604;
  font-family: "Sharp Sans Display No1", sans-serif;
  font-size: ${rem(12)};
  font-weight: 500;
  line-height: 130%;
  text-align: center;
  max-width: ${vwMobile(340)};
  margin: 0 auto;

  ${MEDIA_DESKTOP} {
    font-size: ${rem(16)};
    text-align: left;
    margin: 0;
    max-width: ${vwDesktop(520)};
  }
`;

const CtaWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${vwMobile(20)};

  ${MEDIA_DESKTOP} {
    justify-content: flex-start;
    margin-top: ${vwDesktop(40)};
  }
`;

const PrimaryCta = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${vwMobile(13)} ${vwMobile(32)};
  border-radius: ${rem(432)};
  background: #f6f1e7;
  color: #012604;

  font-family: "GT Alpina";
  font-size: ${rem(12)};
  font-weight: 300;
  line-height: 100%;
  letter-spacing: ${rem(-0.64)};
  text-decoration: none;

  &:hover {
    background: #012604;
    color: #b7db74;
  }

  &:focus-visible {
    outline: 1px solid #012604;
  }
  ${MEDIA_DESKTOP} {
    font-size: ${rem(16)};
    letter-spacing: ${rem(-0.6)};
    padding: ${vwDesktop(16)} ${vwDesktop(32)};
  }
`;

// Video trigger + thumbnail
const VideoTrigger = styled.button`
  width: 100%;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  position: relative;

  &:focus-visible {
    outline: 1px solid #012604;
    border-radius: ${rem(13.234)};
  }
`;

const Thumb = styled.img`
  width: 100%;
  height: ${rem(227)};
  align-self: stretch;
  aspect-ratio: 126/71;
  display: block;
  border-radius: ${rem(13.234)};
  object-fit: cover;

  ${MEDIA_DESKTOP} {
    height: ${rem(360.615)};
    align-self: stretch;
    aspect-ratio: 126/71;
    padding: 0;
  }
`;

const PlayBadge = styled.span`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
`;
const PlayCircle = styled.span`
  width: ${vwMobile(39.703)};
  height: ${vwMobile(39.703)};
  color: #f6f1e7;

  ${MEDIA_DESKTOP} {
    width: ${vwDesktop(31.474)};
    height: ${vwDesktop(31.474)};
  }
`;

const PlayIcon = styled.svg`
  width: 100%;
  height: 100%;
  display: block;
`;

// Modal
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.65);
  display: grid;
  place-items: center;
  padding: ${vwMobile(16)};
`;

const Dialog = styled.div`
  width: min(960px, 100%);
  background: #0b0b0b;
  border-radius: ${rem(18)};
  position: relative;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${rem(10)};
  right: ${rem(10)};
  z-index: 2;

  width: ${rem(40)};
  height: ${rem(40)};
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`;

const PlayerWrap = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #000;
`;

const Iframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
`;

const Html5Video = styled.video`
  width: 100%;
  height: 100%;
  display: block;
`;

const VisuallyHidden = styled.h3`
  position: absolute !important;
  height: 1px;
  width: 1px;
  overflow: hidden;
  clip: rect(1px, 1px, 1px, 1px);
  white-space: nowrap;
`;

export default FiftyFiftyVideo;
